<script setup>
import {computed, reactive, ref} from "vue";
import useForm from "@/composables/useForm";
import PrimaryButton from "@/components/ui/PrimaryButton.vue";
import CustomFieldValue from "@/components/customFields/CustomFieldValue.vue";
import trackersReviewsService from "@/services/trackers-reviews.service";
import useStoreTrigger from "@/composables/useStoreTrigger";
import {useStore} from "vue2-helpers/vuex";
import useErrorsArray from "@/composables/useErrorsArray";

const showModal = ref(false);
const {trackerId} = defineProps(['trackerId']);
const emit = defineEmits(['closed', 'reviewSaved']);
const storeTrigger = useStoreTrigger();
const store = useStore();

const review = reactive({
    message: '',
    interestLevel: 0,
    isNew: true,
    fields: [],
    aggregateFields: [],
});

const companyId = ref(null);
const loading = ref(false);
const newFilesFieldsCount = ref(0);
const dataLoaded = ref(false);

const {populateForm, resetForm, errors, hasErrors, setErrors, clearErrors} = useForm(review);
const {errors: fieldsErrors, populate: populateFieldsErrors, hasErrors: hasFieldsErrors, clear: clearFieldsErrors} = useErrorsArray();
const {errors: aggregateFieldsErrors, populate: populateAggregateFieldsErrors, hasErrors: hasAggregateFieldsErrors, clear: clearAggregateFieldsErrors} = useErrorsArray();

const canSave = computed(() => aggregatedInterestLevel.value || review.interestLevel > 0);
const aggregatedInterestLevel = computed(() => !!review.aggregateFields.length);

async function getData() {
    const {data} = await trackersReviewsService.getEdit(trackerId, companyId.value);
    populateForm(data);
    dataLoaded.value = true;
}

async function deleteReview() {
    if (confirm('Delete Review?')) {
        await trackersReviewsService.delete(trackerId, companyId.value);

        showModal.value = false;
        emit('reviewSaved', companyId.value);
        storeTrigger('setNeedUpdateTrackerLogs');
    }
}

function fieldValueChanged(index, value) {
    review.fields[index].value = value
}

function aggregateFieldValueChanged(index, value) {
    review.aggregateFields[index].value = value
}

async function save() {
    loading.value = true;
    clearErrors();
    clearFieldsErrors();
    clearAggregateFieldsErrors();

    const {data} = await trackersReviewsService.save(trackerId, companyId.value, review);
    setErrors(data.errors);

    populateFieldsErrors(data.errors, 'fields-', review.fields.length);
    populateAggregateFieldsErrors(data.errors, 'aggregateFields-', review.aggregateFields.length);

    if (!hasErrors(data) && !hasFieldsErrors.value && !hasAggregateFieldsErrors.value) {
        let newFilesFields = review.fields.filter(field => field.value.files.some(file => file.id === 0));
        newFilesFieldsCount.value = newFilesFields.length;

        if (newFilesFieldsCount.value) {
            newFilesFields.forEach(field => {
                let formData = new FormData();

                field.value.files.forEach(file => {
                    if (file.id === 0) {
                        formData.append('file[]', file);
                    }
                });

                trackersReviewsService.saveFiles(trackerId, companyId.value, field.field.id, formData).then(() => {
                    newFilesFieldsCount.value--;

                    if (!newFilesFieldsCount.value) {
                        saveComplete();
                    }
                });
            });
        } else {
            saveComplete();
        }
    } else {
        loading.value = false;
    }
}

function saveComplete() {
    emit('reviewSaved', companyId.value);
    showModal.value = false;
    loading.value = false;
}

function open(company_id) {
    resetForm();
    clearErrors();
    clearFieldsErrors();
    clearAggregateFieldsErrors();

    companyId.value = company_id;

    getData();
    showModal.value = true;
}

defineExpose({
    open,
});
</script>

<template>
    <b-modal v-model="showModal" modal-class="modal-w-md modal-rating" no-enforce-focus @hidden="emit('closed')">
        <template #modal-header>
            <h5 v-if="review.isNew" class="modal-title">Ratings</h5>
            <h5 v-else class="modal-title">Edit Review</h5>
            <a v-if="!review.isNew" class="link" @click="deleteReview">Delete Review</a>
            <button @click="showModal = false" class="close"></button>
        </template>

        <div v-if="dataLoaded && !aggregatedInterestLevel" class="rating-section mb-2">
            <h5 class="heading5 heading5--secondary">Your rating:</h5>
            <div class="rating rating-modal">
                <span v-for="index in [1,2,3,4,5]" :key="index" :class="{'rating__current': review.interestLevel >= index}" @click="review.interestLevel = index"></span>
            </div>
        </div>

        <template v-if="!store.getters.settings.disable_review_message">
            <span v-if="errors.message.length" class="error">{{errors.message[0]}}</span>
            <textarea class="form-control--mid form-control mb-3" v-model="review.message"/>
        </template>

        <div v-for="(field, index) in review.fields" :key="'f'+index" class="mt-0 mb-3">
            <h5 class="heading5 heading5--secondary">{{field.field.name}}</h5>

            <span v-if="fieldsErrors[index]?.length" class="error">{{fieldsErrors[index][0]}}</span>

            <CustomFieldValue
                :field="review.fields[index].field"
                :init-value="review.fields[index].value"
                :instantUpdate="false"
                @valueChanged="(value) => fieldValueChanged(index, value)"
            />
        </div>

        <template v-if="review.aggregateFields.length">
            <h5 class="heading5 heading5--secondary text-uppercase">Aggregate Fields</h5>

            <div v-for="(field, index) in review.aggregateFields" :key="'af'+index" class="mt-0 mb-3">
                <div class="d-flex">
                    <h5 class="heading5 heading5--secondary mr-2">{{field.field.name}}</h5>

                    <h5 class="heading5 heading5--gray">{{field.weight}}% weight</h5>
                </div>

                <span v-if="aggregateFieldsErrors[index]?.length" class="error">{{aggregateFieldsErrors[index][0]}}</span>

                <CustomFieldValue
                    :field="review.aggregateFields[index].field"
                    :init-value="review.aggregateFields[index].value"
                    :instantUpdate="false"
                    @valueChanged="(value) => aggregateFieldValueChanged(index, value)"
                />
            </div>
        </template>

        <template #modal-footer>
            <PrimaryButton
                :disabled="!canSave"
                caption="Save"
                :loading="loading"
                loading-caption="Saving..."
                @click="save"
            />
        </template>

    </b-modal>
</template>

<style scoped>

</style>
